import React from 'react';
import { Space, Typography } from 'antd';
import type { MonthPoint } from '../billingApi';
import { BRAND } from '../theme';

const { Text } = Typography;

const NEW_COLOR = BRAND.body;
const CANCEL_COLOR = '#d97706';

const W = 720;
const H = 240;
const PAD = { top: 16, right: 8, bottom: 28, left: 32 };

/** "2026-10" as "Oct". */
const monthName = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });

/** A round number at or above `n` for the top of the axis (4, 5, 10, 20, 50 ...). */
function niceMax(n: number): number {
  if (n <= 4) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  for (const step of [1, 2, 5, 10]) if (n <= step * pow) return step * pow;
  return n;
}

/** New subscriptions and cancellations per month: two bars per month, drawn as inline SVG. */
export const MonthBars: React.FC<{ months: MonthPoint[] }> = ({ months }) => {
  const max = niceMax(
    Math.max(0, ...months.flatMap((m) => [m.new_subscriptions, m.cancellations]))
  );
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / Math.max(months.length, 1);
  const bar = Math.min(18, slot / 2.6);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const ticks = [0, max / 2, max];

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="New subscriptions and cancellations per month, last 12 months"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke="#e8e9f3"
              strokeWidth={1}
            />
            <text x={PAD.left - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#5b6472">
              {t}
            </text>
          </g>
        ))}
        {months.map((m, i) => {
          const cx = PAD.left + slot * i + slot / 2;
          return (
            <g key={m.month}>
              <rect
                x={cx - bar - 1}
                y={y(m.new_subscriptions)}
                width={bar}
                height={PAD.top + plotH - y(m.new_subscriptions)}
                rx={2}
                fill={NEW_COLOR}
              >
                <title>{`${m.month}: ${m.new_subscriptions} new`}</title>
              </rect>
              <rect
                x={cx + 1}
                y={y(m.cancellations)}
                width={bar}
                height={PAD.top + plotH - y(m.cancellations)}
                rx={2}
                fill={CANCEL_COLOR}
              >
                <title>{`${m.month}: ${m.cancellations} cancelled`}</title>
              </rect>
              <text x={cx} y={H - 8} textAnchor="middle" fontSize={11} fill="#5b6472">
                {monthName(m.month)}
              </text>
            </g>
          );
        })}
      </svg>
      <Space size={16} style={{ marginTop: 8 }}>
        <Space size={6}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: NEW_COLOR }} />
          <Text type="secondary">New</Text>
        </Space>
        <Space size={6}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: CANCEL_COLOR }} />
          <Text type="secondary">Cancelled</Text>
        </Space>
      </Space>
    </div>
  );
};
